import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/compat';

import { DirectionalExpandArrowIcon } from '../PRIVATE_ThemedIcons/DirectionalExpandArrowIcon';
import { useId } from '../hooks/UNSAFE_useId';

import { BaseMenuItem } from './MenuItem';
import { DropdownMenu } from './DropdownMenu';
import { useMenuContainerContext } from './useMenuContainerContext';
import { SubmenuProps } from './Submenu';
import { scaleVars } from '@oracle/oraclejet-internal-theme-contract/themeContract.css';
import { useScale } from '#hooks/UNSAFE_useScale';

export const DropdownSubmenu = ({ label, children, startIcon, itemKey }: SubmenuProps) => {
  //Id of the submenu that will be used to connect via aria-owns, menuItem with its submenu
  const submenuId = useId();

  const preProcessedMenuItemID = useId();
  //MenuItem id
  const menuItemId = useMemo(
    () => `oj-menu-item-${preProcessedMenuItemID}`,
    [preProcessedMenuItemID]
  );

  const { changeKey, currentKey } = useMenuContainerContext();

  const submenuMenuItemRef = useRef<HTMLAnchorElement | null>(null);
  const dropdownSubmenuRef = useRef<HTMLDivElement | null>(null);

  const [isOpen, setIsOpen] = useState(false);

  const [currentFocus, setCurrentFocus] = useState<'menu' | 'firstItem' | 'none'>('none');

  const [pxPerUnit, setPxPerUnit] = useState(DEFAULT_PX_PER_UNIT);
  const scale = useScale();

  useEffect(() => {
    // Important to have scale as a posible reason for running this effect since changing scale would produce a need of updating px per unit
    if (!scale || !scaleVars?.size?.units) {
      setPxPerUnit(0.25 * remInPx());
    } else {
      // We asume scale vars has the form of var(x), due to that we obtain string inside parenthesis, if not found empty string is passed
      const cssVar = /\(([^)]+)\)/.exec(scaleVars.size.units)?.[1] || '';
      // We try to get css var value, if this could not be obtained we use 0.25rem as fallback value
      const valueofCSSVar = window
        .getComputedStyle(submenuMenuItemRef.current as Element)
        .getPropertyValue(cssVar);
      if (valueofCSSVar.endsWith('px')) {
        setPxPerUnit(parseFloat(valueofCSSVar));
      } else if (valueofCSSVar.endsWith('rem')) {
        setPxPerUnit(parseFloat(valueofCSSVar) * remInPx());
      } else {
        setPxPerUnit(0.25 * remInPx());
      }
    }
  }, [scale]);

  useEffect(() => {
    if (menuItemId !== currentKey) {
      setIsOpen(false);
    }
  }, [currentKey, menuItemId]);

  //This function is used when pressing submenuitem and hovering over it
  const openSubmenuAction = useCallback((e: Event) => {
    submenuMenuItemRef.current?.focus();
    //If not click/touch we set the focus on the first item
    setIsOpen(true);
    if (e.type === 'keyup') {
      setCurrentFocus('firstItem');
    } else {
      setCurrentFocus('none');
    }
  }, []);

  //TODO: There is a bug in a Icon, once that bug is solved we have to check this again. JET-56980
  const hoverStatus = useRef<{ recentClose: boolean; pendingUnhover: boolean }>({
    recentClose: false,
    pendingUnhover: false
  });

  return (
    <>
      <BaseMenuItem
        itemKey={itemKey}
        startIcon={startIcon}
        id={menuItemId}
        submenuId={submenuId}
        ref={submenuMenuItemRef}
        label={label}
        endIcon={<DirectionalExpandArrowIcon></DirectionalExpandArrowIcon>}
        type="submenu"
        role="menuitem"
        onAction={openSubmenuAction}
        hoverInfo={{
          //Specific hover events of submenuItem
          hoverProps: {
            onMouseEnter: (e: MouseEvent) => {
              openSubmenuAction(e);
            }
          },
          hoverStatus
        }}
        //Open submenu using right/left key
        openSubmenu={() => {
          setIsOpen(true);
          setCurrentFocus('firstItem');
        }}
        submenuRef={dropdownSubmenuRef}
        isSubmenuOpen={isOpen}></BaseMenuItem>

      <DropdownMenu
        isOpen={isOpen}
        menuLevel="sub"
        menuId={submenuId}
        //Close submenu with left/right key
        closeSubmenu={() => {
          hoverStatus.current.recentClose = true;
          changeKey({ key: currentKey, reason: 'keyboard' });
          setTimeout(() => {
            hoverStatus.current.recentClose = false;
          }, HOVER_SHIFT_TIMEOUT);
          setIsOpen(false);
          //CurrentKey can be the same as submenuItem, because of that we explicitly focus it
          submenuMenuItemRef.current?.focus();
        }}
        /*For now we are exposing ref if this is exposed at some point we would look for other
            mechanisms to use this ref */
        ref={dropdownSubmenuRef}
        anchorRef={submenuMenuItemRef}
        defaultPlacement="end-top"
        currentFocus={currentFocus}
        offsetValue={{
          mainAxis: -(2 * pxPerUnit), //TODO: need to figure out how to make this themable.
          crossAxis: -(2 * pxPerUnit + 1)
        }}
        flipOptions={{
          crossAxis: true,
          mainAxis: true,
          fallbackPlacements: ['start-top', 'end-bottom', 'start-bottom']
        }}>
        {children}
      </DropdownMenu>
    </>
  );
};

const HOVER_SHIFT_TIMEOUT = 100;
const DEFAULT_PX_PER_UNIT = 4;
const remInPx = () => {
  return parseInt(window.getComputedStyle(document.documentElement).fontSize || '16px', 10);
};
